// =============================================================================
//  Copyright : 2026 semantics GmbH
//  Date      : $Date: 2026-08-20 08:02:31 +0200 (Thu, 20 Aug 2026) $
//  Author    : Olli Powroznik
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 164960 $
//  Purpose   : Toolbar layout algorithms and React children utilities
// =============================================================================
// ---Third-party imports
import React from 'react';
// * Minimum items visible before collapsing a group.
export const MIN_VISIBLE = 2;
const sumWidth = (items, count)=>{
    let total = 0;
    for(let i = 0; i < count; i++)total += items[i] || 0;
    return total;
};
const groupWidth = (group, visibleCount, collapsed, moreWidth, collapsedWidth)=>{
    let { items, chrome = 0, title = 0, collapsedW, pad = 0 } = group;
    let row = collapsed ? collapsedW ?? collapsedWidth ?? moreWidth : sumWidth(items, visibleCount) + (visibleCount < items.length ? moreWidth : 0) + chrome;
    return Math.max(row, title) + pad;
};
// * Greedy layout algorithm: hides items and collapses groups right-to-left to fit container.
export const computeToolbarLayout = ({ groups, containerWidth, moreWidth, collapsedWidth = moreWidth, minVisible = MIN_VISIBLE })=>{
    let layout = groups.map((g)=>({
            visibleCount: g.items.length,
            collapsed: false
        }));
    let width = (g, visibleCount, collapsed)=>groupWidth(g, visibleCount, collapsed, moreWidth, collapsedWidth);
    let current = groups.reduce((acc, g)=>acc + width(g, g.items.length, false), 0);
    if (current <= containerWidth) return {
        layout,
        overflow: false
    };
    for(let gi = groups.length - 1; gi >= 0; gi--){
        if (current <= containerWidth) break;
        let g = groups[gi];
        if (width(g, minVisible, false) < width(g, g.items.length, false)) {
            while(current > containerWidth && layout[gi].visibleCount > minVisible){
                let before = width(g, layout[gi].visibleCount, false);
                layout[gi].visibleCount--;
                current -= before - width(g, layout[gi].visibleCount, false);
            }
        }
        if (current > containerWidth) {
            let before = width(g, layout[gi].visibleCount, false);
            let after = width(g, layout[gi].visibleCount, true);
            if (after < before) {
                layout[gi].collapsed = true;
                current -= before - after;
            }
        }
    }
    return {
        layout,
        overflow: current > containerWidth
    };
};
// * Normalize React children tree: unwrap Fragments and DebugFeatures into flat item array.
export const flattenChildren = (children)=>{
    if (!children) return [];
    children = Array.isArray(children) ? children : [
        children
    ];
    let flat = [];
    children.forEach((child, i)=>{
        if (!child) return;
        if (Array.isArray(child)) {
            flat.push(...flattenChildren(child));
        } else if (child.type?.displayName === 'DebugFeature') {
            flattenChildren(child.props.children).forEach((c, j)=>{
                flat.push(/*#__PURE__*/ React.cloneElement(child, {
                    key: `df-${i}-${j}`,
                    children: c
                }));
            });
        } else if (child.type === React.Fragment) {
            flat.push(...flattenChildren(child.props.children));
        } else {
            flat.push(child);
        }
    });
    return flat;
};
// * Recursively count active/selected items in a tree.
export const countActive = (items)=>{
    let count = 0;
    let check = (props)=>{
        if (props.active === true || props.value === true) count++;
        let kids = props.children;
        if (Array.isArray(kids)) kids.forEach((c)=>c?.props && check(c.props));
        else if (kids?.props) check(kids.props);
    };
    items.forEach((item)=>item?.props && check(item.props));
    return count;
};
// * Extract ToolbarGroup elements and convert to normalized layout data structure.
export const readGroups = (children, isDebugOn = false)=>{
    let list = Array.isArray(children) ? children : children ? [
        children
    ] : [];
    let groups = [];
    list.forEach((child, i)=>{
        if (!child) return;
        let debug = child.type?.displayName === 'DebugFeature';
        if (debug && !isDebugOn) return;
        let groupEl = debug ? Array.isArray(child.props.children) ? child.props.children[0] : child.props.children : child;
        if (groupEl?.type?.displayName !== 'ToolbarGroup' || groupEl.props.show === false) return;
        groups.push({
            key: child.key ?? `g-${i}`,
            debug,
            props: groupEl.props,
            items: flattenChildren(groupEl.props.children)
        });
    });
    return groups;
};
// * Compare two layout states for equality.
export const sameLayout = (a, b)=>a === b || !!a && !!b && a.length === b.length && a.every((e, i)=>e.visibleCount === b[i].visibleCount && e.collapsed === b[i].collapsed);
// * Check if element is a toolbar separator by displayName.
export const isSeparator = (child)=>{
    if (child?.type?.displayName === 'DebugFeature') child = child.props.children;
    return child?.type?.displayName === 'ToolbarSeparator';
};
// * Filter item indices, removing leading and trailing separators.
export const keptItemIndices = (items)=>{
    let keep = [];
    let pendingSep = -1;
    let seenItem = false;
    items.forEach((item, i)=>{
        if (isSeparator(item)) {
            if (seenItem) pendingSep = i;
        } else {
            if (pendingSep >= 0) {
                keep.push(pendingSep);
                pendingSep = -1;
            }
            keep.push(i);
            seenItem = true;
        }
    });
    return keep;
};
// * Return items filtered to only those at kept indices (separators removed).
export const trimSeparators = (items)=>keptItemIndices(items).map((i)=>items[i]);
